import {
	Box,
	MenuItem,
	Paper,
	TextField,
	Typography,
	Tooltip,
} from '@mui/material';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import PropTypes from 'prop-types';
import {
	EMAIL_SYNC_CADENCE_INCREMENT_MINUTES,
	EMAIL_SYNC_CADENCE_MAX_MINUTES,
	EMAIL_SYNC_CADENCE_MIN_MINUTES,
} from './emailSyncCadence';

const CADENCE_INPUT_CONSTRAINTS = {
	minutes: {
		min: EMAIL_SYNC_CADENCE_MIN_MINUTES,
		max: EMAIL_SYNC_CADENCE_MAX_MINUTES,
		step: EMAIL_SYNC_CADENCE_INCREMENT_MINUTES,
	},
	hours: {
		min: 1,
		max: 24,
		step: 1,
	},
};

function AutomaticSyncCadence({
	value,
	unit,
	validation,
	onValueChange,
	onUnitChange,
	readOnly = false,
}) {
	const cadenceInputProps = {
		...CADENCE_INPUT_CONSTRAINTS[unit],
		'data-testid': 'email-sync-cadence-input',
	};

	const handleValueChange = (event) => {
		const { value: inputValue } = event.target;

		onValueChange?.(inputValue === '' ? '' : Number(inputValue));
	};

	const handleUnitChange = (event) => {
		onUnitChange?.(event.target.value);
	};

	return (
		<Paper
			variant='outlined'
			sx={{
				px: 3,
				py: 2,
				borderRadius: 0.8,
				backgroundColor: 'background.default',
			}}
		>
			<Box>
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
					<Typography
						variant='subtitle1'
						sx={{
							fontWeight: 600,
							mb: 0.1,
							fontSize: '0.9rem',
						}}
					>
						Automatic Sync Cadence
					</Typography>

					{readOnly && (
						<Tooltip
							title='This setting is controlled by your tenant admin'
							placement='top'
							arrow
							slotProps={{
								tooltip: {
									sx: {
										borderRadius: '5px',
									},
								},
							}}
						>
							<InfoOutlined
								aria-label='Automatic sync cadence information'
								sx={{
									fontSize: '1rem',
									color: 'text.secondary',
									cursor: 'help',
									mb: 0.3,
								}}
							/>
						</Tooltip>
					)}
				</Box>

				<Typography
					variant='body2'
					color='text.secondary'
					sx={{
						fontSize: '0.8rem',
						mb: 1,
					}}
				>
					How often Email Sync checks a connected inbox for new emails. Default
					is 5 minutes, up to a maximum of 24 hours.
				</Typography>

				<Box
					sx={{
						display: 'flex',
						alignItems: 'flex-start',
						gap: 1,
					}}
				>
					<TextField
						type='number'
						size='small'
						value={value}
						onChange={handleValueChange}
						disabled={readOnly}
						error={!readOnly && !validation.isValid}
						helperText={!readOnly ? validation.errorMessage : undefined}
						slotProps={{
							htmlInput: cadenceInputProps,
						}}
						sx={{ width: 120 }}
					/>

					<TextField
						select
						size='small'
						value={unit}
						onChange={handleUnitChange}
						disabled={readOnly}
						slotProps={{
							select: {
								'data-testid': 'email-sync-cadence-unit-select',
							},
						}}
						sx={{ width: 130 }}
					>
						<MenuItem value='minutes'>Minutes</MenuItem>
						<MenuItem value='hours'>Hours</MenuItem>
					</TextField>
				</Box>
			</Box>
		</Paper>
	);
}

AutomaticSyncCadence.propTypes = {
	value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
	unit: PropTypes.oneOf(['minutes', 'hours']).isRequired,
	validation: PropTypes.shape({
		isValid: PropTypes.bool.isRequired,
		errorMessage: PropTypes.string,
		value: PropTypes.number.isRequired,
	}).isRequired,
	onValueChange: PropTypes.func,
	onUnitChange: PropTypes.func,
	readOnly: PropTypes.bool,
};

export default AutomaticSyncCadence;
